import { useEffect } from "react";
import { Outlet, Navigate } from "react-router";
import { useAuth } from "react-oidc-context";

import { useUserRoles } from "@/hooks/useUserRoles";

const parseDevUserIds = (value?: string): string[] => (
  value ?? ""
).split(",").map((id) => id.trim()).filter(Boolean);

const getRuntimeEnv = (): Record<string, string | undefined> | undefined => {
  return typeof window === "undefined"
    ? undefined
    : (window as Window & {
      __ENV__?: Record<string, string | undefined>;
    }).__ENV__;
};

const isExternalRegisterAdminDevMode = (): boolean => {
  const runtimeEnvironment = getRuntimeEnv()?.ENV?.toLowerCase();

  return import.meta.env.DEV
    || import.meta.env.MODE !== "production"
    || Boolean(runtimeEnvironment && runtimeEnvironment !== "prod" && runtimeEnvironment !== "production");
};

const getExternalRegisterAdminDevUserIds = (): string | undefined => {
  const runtimeEnv = getRuntimeEnv();

  return import.meta.env.VITE_EXTERNAL_REGISTER_ADMIN_DEV_USER_IDS
    || runtimeEnv?.VITE_EXTERNAL_REGISTER_ADMIN_DEV_USER_IDS
    || runtimeEnv?.EXTERNAL_REGISTER_ADMIN_DEV_USER_IDS;
};

export const ExternalRegisterSettingsAdminGuard = () => {
  const auth = useAuth();
  const { isAdmin, userRoles = [] } = useUserRoles();
  const authDecisionPending = Boolean(auth?.isLoading || (auth?.isAuthenticated && !auth.user));
  const profile = auth?.user?.profile as {
    oid?: string;
    sub?: string;
    roles?: string[];
    groups?: string[];
  } | undefined;
  const devUserIds = parseDevUserIds(getExternalRegisterAdminDevUserIds());
  const isDevMode = isExternalRegisterAdminDevMode();
  const currentUserIds = [profile?.oid, profile?.sub].filter((id): id is string => Boolean(id));
  const devUserIdAllowed = isDevMode && currentUserIds.some((id) => devUserIds.includes(id));

  useEffect(() => {
    if (!isDevMode || authDecisionPending) return;

    console.info("[ExternalRegisterSettingsAdminGuard] auth identity diagnostics", {
      oid: profile?.oid,
      sub: profile?.sub,
      roles: profile?.roles ?? userRoles,
      groups: profile?.groups,
      isAdmin,
      devBypassConfigured: devUserIds.length > 0,
      devUserIdAllowed,
    });
  }, [authDecisionPending, devUserIdAllowed, devUserIds.length, isAdmin, profile?.groups, profile?.oid, profile?.roles, profile?.sub, userRoles]);

  if (authDecisionPending) {
    return null;
  }

  if (!isAdmin && !devUserIdAllowed) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

export default ExternalRegisterSettingsAdminGuard;
